import type { TableColumnType, TableRowType } from 'components/editor/grid/reduxStore/table.types';
import { isGeneratedRow } from 'components/editor/grid/reduxStore/crmRowMarkers';
import type { TipTapJsonNode } from './crmChipDocs';
import { parseTipTapDoc } from './crmChipDocs';
import { validCrmFieldInstanceEntries } from './crmFieldInstancesGuard';

// `template` values reach here from persisted crmFieldInstances (untyped storage, see
// crmFieldInstancesGuard.ts) and from unchecked casts over clipboard JSON — never assume
// it's a string. A non-string or empty-string input has no valid array identity, so it
// returns undefined rather than throwing or silently matching everything. (An empty string
// must be excluded explicitly: ''.slice(0, ''.lastIndexOf('.')) is '', not undefined, which
// would otherwise read as a valid — and always-conflicting — identity.)
export const crmArrayIdentity = (template: unknown): string | undefined =>
  typeof template === 'string' && template !== '' ? template.slice(0, template.lastIndexOf('.')) : undefined;

export function rowCrmArrayConflict(row: TableRowType | undefined, incomingTemplate: string | null | undefined): boolean {
  if (!row?.crmFieldInstances || !incomingTemplate) return false;
  const incomingArray = crmArrayIdentity(incomingTemplate);
  // No valid identity to conflict against — nothing to compare, so no conflict.
  if (incomingArray === undefined) return false;
  return validCrmFieldInstanceEntries(row).some(([, instances]) =>
    instances.some((instance) => {
      const instanceArray = crmArrayIdentity(instance?.template);
      // A non-string persisted template has no identity either — skip it, same as the
      // existing guard skips a malformed (non-array) crmFieldInstances value instead of
      // treating it as an automatic conflict.
      return instanceArray !== undefined && instanceArray !== incomingArray;
    })
  );
}

const collectChips = (node: TipTapJsonNode, chips: TipTapJsonNode[]): void => {
  if (node.type === 'variable') chips.push(node);
  node.content?.forEach((child) => collectChips(child, chips));
};

export function clearStaleConflictFlags(row: TableRowType, columns: TableColumnType[]): TableRowType {
  if (isGeneratedRow(row)) return row;

  const promotedArrays: string[] = [];
  const patch: Partial<TableRowType> = {};

  columns.forEach(({ field }) => {
    const value = row[field];
    if (typeof value !== 'string' || !value.includes('"isConflict":true')) return;
    const doc = parseTipTapDoc(value);
    if (!doc) return;

    const chips: TipTapJsonNode[] = [];
    collectChips(doc, chips);
    let changed = false;
    chips.forEach((chip) => {
      if (!chip.attrs?.isConflict) return;
      const identity = crmArrayIdentity(chip.attrs.template);
      if (identity === undefined) return;
      if (rowCrmArrayConflict(row, String(chip.attrs.template)) || promotedArrays.some((promoted) => promoted !== identity)) return;
      chip.attrs.isConflict = false;
      promotedArrays.push(identity);
      changed = true;
    });
    if (changed) patch[field] = JSON.stringify(doc);
  });

  return Object.keys(patch).length > 0 ? { ...row, ...patch } : row;
}
